import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Building2, Lock, Mail, ShieldCheck } from "lucide-react";

const ROLES = ["Staff", "Reviewer", "Officer", "Admin"];

export default function Login() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("Staff");
  const navigate = useNavigate();

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    try {
      await login(email.trim(), password, role);
      toast.success(`Logged in as ${role}`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      {/* Left visual panel */}
      <div className="ssrd-banner text-white p-10 lg:p-14 flex-col justify-between hidden lg:flex">
        <div className="flex items-center gap-3">
          <div className="size-12 rounded-md bg-white/10 border border-white/20 grid place-items-center">
            <Building2 className="size-6 text-blue-200" />
          </div>
          <div>
            <div className="text-[10px] tracking-widest text-blue-200 uppercase">Government of Gujarat</div>
            <div className="font-semibold text-lg">Revenue Department · SSRD</div>
          </div>
        </div>

        <div className="space-y-4 max-w-md">
          <h1 className="text-4xl sm:text-5xl tracking-tight font-bold leading-[1.05]">
            Remi8-assisted<br/>order drafting,<br/>officer-approved.
          </h1>
          <p className="text-blue-100/80 leading-relaxed">
            A proof of concept for drafting Gujarati SSRD orders in
            land/revenue appeal cases. The system assists drafting —
            final judgment, signing and issuance remain with authorized
            department officers.
          </p>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div className="rounded-md border border-white/15 bg-white/5 p-3">
              <div className="text-[10px] tracking-wider text-blue-200 uppercase">OCR Engine</div>
              <div className="text-sm font-semibold">Remi8 Vision</div>
              <div className="text-[11px] text-blue-100/70">Gujarati gu-IN</div>
            </div>
            <div className="rounded-md border border-white/15 bg-white/5 p-3">
              <div className="text-[10px] tracking-wider text-blue-200 uppercase">Drafting</div>
              <div className="text-sm font-semibold">Remi8 AI Engine</div>
              <div className="text-[11px] text-blue-100/70">Remi8-128B</div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs text-blue-100/70">
          <ShieldCheck className="size-4" />
          <span>DPDP Act, 2023 aligned · India data residency · Audit logged</span>
        </div>
      </div>

      {/* Right form panel */}
      <div className="bg-background p-6 lg:p-14 flex items-center">
        <div className="w-full max-w-md mx-auto">
          <div className="mb-8 lg:hidden flex items-center gap-3">
            <div className="size-10 rounded-md bg-primary text-white grid place-items-center"><Building2 className="size-5" /></div>
            <div>
              <div className="text-[10px] tracking-widest text-primary uppercase">Govt. of Gujarat</div>
              <div className="font-semibold">SSRD Order Drafting · Remi8</div>
            </div>
          </div>
          <div className="space-y-1 mb-6">
            <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Secure Login</div>
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">Sign in to your account</h2>
            <p className="text-sm text-muted-foreground">Department staff and authorized officers only.</p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email / Username</Label>
              <div className="relative">
                <Mail className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email" type="email" className="pl-9"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="staff@ssrd.gov.in"
                  data-testid="login-email-input"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password" type="password" className="pl-9"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="login-password-input"
                  required
                />
              </div>
            </div>

            <div>
              <Label>Role (Prototype)</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="login-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r} data-testid={`role-option-${r}`}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-10 text-[11px] text-muted-foreground leading-relaxed border-t pt-4">
            Remi8-assisted drafting only. Final approval remains with the
            authorized department officer.
          </p>
        </div>
      </div>
    </div>
  );
}
